import { useCallback, useMemo } from 'react';
import {
    THBCalloutBannerIllustrationAspectRatio,
    THBCalloutBannerIllustrationOrientation,
    THBCalloutBannerSize,
    THBCalloutBannerStyles,
    THBCalloutBannerTheme,
    THBCalloutBannerType
} from './HBCalloutBanner.types';
import colors from '../../styles/colors';
import { vars } from '../../theme';

const STYLES_PER_TYPE_PER_THEME: Record<
    THBCalloutBannerType,
    Record<
        THBCalloutBannerTheme,
        Omit<
            THBCalloutBannerStyles,
            | 'padding'
            | 'buttonSize'
            | 'titleType'
            | 'descriptionType'
            | 'illustrationPlaceholder'
            | 'linkButtonSize'
        >
    >
> = {
    neutral: {
        neutral: {
            backgroundColor: vars.colors.surface.container,
            shadow: true,
            // Unsupported: colors.purpleHeart — no matching semantic purple accent (see .claude/rules/styling.md)
            accentLineColor: colors.purpleHeart,
            textColor: 'text.primary',
            iconColor: vars.colors.text.primary,
            primaryActionButtonTheme: 'primary',
            primaryActionLinkButtonTheme: 'solid',
            secondaryActionButtonTheme: 'secondary-invisible-secondary',
            closeColor: vars.colors.text.primary
        },
        accent: {
            backgroundColor: vars.colors.surface.purple,
            shadow: false,
            // Unsupported: colors.purpleHeart — no matching semantic purple accent (see .claude/rules/styling.md)
            accentLineColor: colors.purpleHeart,
            textColor: 'text.primary',
            iconColor: vars.colors.text.primary,
            primaryActionButtonTheme: 'primary',
            primaryActionLinkButtonTheme: 'solid',
            secondaryActionButtonTheme: 'secondary-invisible-secondary',
            closeColor: vars.colors.text.primary
        }
    },
    brand: {
        neutral: {
            // Unsupported: colors.merino500 — warm brand cream, no semantic token (see .claude/rules/styling.md)
            backgroundColor: colors.merino500,
            shadow: false,
            accentLineColor: 'transparent',
            textColor: 'text.primary',
            iconColor: vars.colors.text.primary,
            primaryActionButtonTheme: 'primary',
            primaryActionLinkButtonTheme: 'solid',
            secondaryActionButtonTheme: 'secondary-invisible-secondary',
            closeColor: vars.colors.text.primary
        },
        accent: {
            backgroundColor: vars.colors.surface.itemInverted,
            shadow: false,
            accentLineColor: 'transparent',
            textColor: 'text.invertedPrimary',
            iconColor: vars.colors.text.invertedPrimary,
            primaryActionButtonTheme: 'secondary-solid-primary',
            primaryActionLinkButtonTheme: 'secondary',
            secondaryActionButtonTheme: 'secondary-translucent-secondary',
            closeColor: vars.colors.text.invertedPrimary
        }
    }
} as const;

const STYLES_PER_SIZE: Record<
    THBCalloutBannerSize,
    Pick<
        THBCalloutBannerStyles,
        | 'padding'
        | 'buttonSize'
        | 'titleType'
        | 'descriptionType'
        | 'linkButtonSize'
    >
> = {
    sm: {
        padding: 16,
        titleType: 'title16',
        descriptionType: 'body14',
        buttonSize: 'xs',
        linkButtonSize: 'sm'
    },
    md: {
        padding: 20,
        titleType: 'title18',
        descriptionType: 'body16',
        buttonSize: 'sm',
        linkButtonSize: 'md'
    },
    lg: {
        padding: 24,
        titleType: 'display24',
        descriptionType: 'body18',
        buttonSize: 'md',
        linkButtonSize: 'lg'
    }
} as const;

const SQUARE_ILLUSTRATION_PLACEHOLDER_MAP: Record<
    THBCalloutBannerSize,
    number
> = {
    sm: 205,
    md: 225,
    lg: 250
} as const;

const ASPECT_RATIO_MAP: Record<
    THBCalloutBannerIllustrationAspectRatio,
    number
> = {
    '1:1': 1,
    '4:3': 4 / 3,
    '3:2': 3 / 2,
    '16:9': 16 / 9,
    '2:1': 2
};

export const useHBCalloutBannerStyles = (
    type: THBCalloutBannerType,
    theme: THBCalloutBannerTheme,
    size: THBCalloutBannerSize,
    illustrationAspectRatio: THBCalloutBannerIllustrationAspectRatio,
    illustrationOrientation: THBCalloutBannerIllustrationOrientation
): THBCalloutBannerStyles => {
    const getIllustrationPlaceholder = useCallback(
        (bannerSize: THBCalloutBannerSize): number => {
            const squareIllustrationPlaceholder =
                SQUARE_ILLUSTRATION_PLACEHOLDER_MAP?.[bannerSize] ??
                SQUARE_ILLUSTRATION_PLACEHOLDER_MAP.sm;

            const aspectRatioOrientationPower =
                illustrationOrientation === 'landscape' ? 1 : -1;

            const aspectRatio = Math.pow(
                ASPECT_RATIO_MAP?.[illustrationAspectRatio] ??
                    ASPECT_RATIO_MAP['1:1'],
                aspectRatioOrientationPower
            );

            const contentPadding =
                STYLES_PER_SIZE[bannerSize].padding ??
                STYLES_PER_SIZE.sm.padding;

            return (
                Math.round(squareIllustrationPlaceholder * aspectRatio) +
                contentPadding
            );
        },
        [illustrationAspectRatio, illustrationOrientation]
    );

    return useMemo(() => {
        const stylesPerTypePerTheme =
            STYLES_PER_TYPE_PER_THEME?.[type]?.[theme] ??
            STYLES_PER_TYPE_PER_THEME.neutral.neutral;

        let sizeLimit = size;
        if (type === 'neutral' && size === 'lg') {
            sizeLimit = 'md';
        }

        const stylesPerSize =
            STYLES_PER_SIZE?.[sizeLimit] ?? STYLES_PER_SIZE.sm;

        const illustrationPlaceholder = getIllustrationPlaceholder(sizeLimit);

        return {
            ...stylesPerTypePerTheme,
            ...stylesPerSize,
            illustrationPlaceholder
        };
    }, [getIllustrationPlaceholder, size, theme, type]);
};
